import React from "react";
import styles from "./index.module.scss";

/**
 * @typedef {Object} BannerProps
 *
 * @property {string} title - Main heading text shown on the banner
 * @property {string} description - Supporting description text
 * @property {string} ctaLabel - CTA button label text
 * @property {string} ctaLink - CTA navigation URL
 * @property {string} imageSrc - Banner image source URL
 * @property {string} imageAlt - Alt text for the banner image (accessibility)
 * @property {boolean} reverseOnDesktop - If true, reverses layout on desktop view
 */
const ContentWithImageBanner = ({ contentWithImageBannerProps = {} }) => {
  const {
    title = "",
    description = "",
    image = {},
    cta = {},
    reverseOnDesktop = false,
    showBottomLine = false,
  } = contentWithImageBannerProps || {};

  const { url = "", openerType = "" } = cta?.fields || {};
  const targets = openerType === "New Tab" || url?.includes("http") ? "_blank" : "_self";
  const bottomLineClass = showBottomLine ? styles.bottomLine : "";

  return (
    <section
      className={` ${styles.contentWithBannerContainer}  ${bottomLineClass} w-full bg-[#202020] text-white`}
    >
      <div className="mx-auto w-full max-w-full md:max-w-screen-xl pt-[45px] pb-[20px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* TEXT */}
          <div
            className={`order-2 md:order-1 ${contentWithImageBannerProps.reverseOnDesktop ? "md:order-2" : ""}`}
          >
            <h2 className="font-[HelveticaLight,Helvetica,sans-serif] text-[25px] md:text-[40px] font-normal leading-[48px] text-white min-[1100px]:pb-[15px]">
              {title}
            </h2>

            <p
              className={`${styles.contentWithBannerDescription} text-sm md:text-base text-neutral-200 max-w-prose`}
            >
              {description}
            </p>

            {cta?.fields?.label && (
              <a
                href={cta?.fields?.url}
                target={targets}
                className={`${styles.contentWithBannerCta} mt-7.5 md:w-[50%] w-[100%] inline-flex items-center justify-center rounded-md border border-white/90 bg-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase !text-black !no-underline visited:!text-black hover:!text-black hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition-colors`}
              >
                {cta?.fields?.label}
              </a>
            )}
          </div>

          {/* IMAGE */}
          {image?.fields?.imageUrl && (
            <div
              className={`
              w-full
              order-1 md:order-2
              ${reverseOnDesktop ? "md:order-1" : ""}
            `}
            >
              <div className="overflow-hidden bg-neutral-800 md:rounded-md">
                <div className="aspect-[4/3] md:aspect-[4/3]">
                  <img
                    src={image?.fields?.imageUrl}
                    alt={image?.fields?.imageUrl}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContentWithImageBanner;
